import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, num, formatApiErrorDetail, API, evalFormula } from "../lib/api";
import { GlassCard } from "../components/GlassCard";
import { Spinner, SpinnerBlock } from "../components/Spinner";
import {
  Search,
  Lock,
  Download,
  Plus,
  ShieldAlert,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  User,
  Info,
  Zap,
  Eye,
  EyeOff,
  MoreHorizontal,
  Star,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import CounterpartyCombobox from "../components/CounterpartyCombobox";
import FinalizeLogDialog from "../components/FinalizeLogDialog";

export default function Entries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [level, setLevel] = useState("owner");
  const [selectedId, setSelectedId] = useState(searchParams.get("account") || null);
  const [ledger, setLedger] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(""); // filter entries by name/note/amount
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [finalizeLogOpen, setFinalizeLogOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [msg, setMsg] = useState(null);

  // New-entry row state
  const [newRow, setNewRow] = useState({ counterparty: "", amount: "", note: "" });
  const [stickyCounterparty, setStickyCounterparty] = useState(false);
  const newCounterpartyRef = useRef(null);
  const newAmountRef = useRef(null);
  const newNoteRef = useRef(null);

  // Recent counterparties — per-account last-5, persisted to localStorage so
  // it survives reloads. Scoped by workspace id so shared accounts don't leak.
  const recentKey = useMemo(() => {
    const ws = localStorage.getItem("workspace_id") || "own";
    return selectedId ? `recent-cp:${ws}:${selectedId}` : null;
  }, [selectedId]);
  const [recentCounterparties, setRecentCounterparties] = useState([]);
  useEffect(() => {
    if (!recentKey || !selectedId) { setRecentCounterparties([]); return; }
    // Start from anything the user has explicitly used in-session/localStorage,
    // then top up from the current ledger's actual entry history so the row is
    // useful on the very first visit — no need to post an entry to see chips.
    let stored = [];
    try {
      const raw = localStorage.getItem(recentKey);
      stored = raw ? JSON.parse(raw) : [];
    } catch { stored = []; }
    // Walk the ledger backwards (newest first) picking unique counterparties.
    const derived = [];
    const entries = ledger?.entries || [];
    // Loop already-sorted-descending entries; if the API returns ascending,
    // we still pick uniques so the outcome is fine either way.
    for (let i = entries.length - 1; i >= 0 && derived.length < 8; i--) {
      const e = entries[i];
      const cp = e.from_account_id === selectedId ? e.to_account_id : e.from_account_id;
      if (cp && !derived.includes(cp)) derived.push(cp);
    }
    const merged = [];
    for (const id of [...stored, ...derived]) {
      if (!merged.includes(id) && merged.length < 5) merged.push(id);
    }
    setRecentCounterparties(merged);
  }, [recentKey, ledger, selectedId]);
  const pushRecentCounterparty = useCallback((cpId) => {
    if (!recentKey || !cpId) return;
    setRecentCounterparties((prev) => {
      const next = [cpId, ...prev.filter((id) => id !== cpId)].slice(0, 5);
      try { localStorage.setItem(recentKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [recentKey]);

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data.accounts || []);
    setLevel(data.workspace_level || "owner");
    // One-shot: consume ?account=… from URL, then wipe it so user can freely switch accounts.
    const urlAccount = searchParams.get("account");
    if (urlAccount && data.accounts?.some((a) => a.id === urlAccount)) {
      setSelectedId(urlAccount);
      setSearchParams({}, { replace: true });
    } else if (!selectedId && data.accounts?.length) {
      setSelectedId(data.accounts[0].id);
    }
  }, [selectedId, searchParams, setSearchParams]);

  const [showFinalized, setShowFinalized] = useState(false);

  // Every time the selected account changes we default back to "older hidden"
  // so switching accounts doesn't inherit a previous account's toggle state.
  // Also reset the "Keep" (sticky counterparty) toggle + counterparty field so
  // a stale counterparty from the previous account doesn't leak into the new
  // account's new-entry row — Keep is scoped to WITHIN one account view only.
  useEffect(() => {
    setShowFinalized(false);
    setStickyCounterparty(false);
    setNewRow({ counterparty: "", amount: "", note: "" });
  }, [selectedId]);

  const loadLedger = useCallback(async (id, opts = {}) => {
    if (!id) return;
    const includeFinalized = opts.includeFinalized ?? showFinalized;
    setBusy(true);
    try {
      const { data } = await api.get(`/entries/by-account/${id}`, {
        params: { include_finalized: includeFinalized ? "true" : "false" },
      });
      setLedger(data);
    } finally {
      setBusy(false);
    }
  }, [showFinalized]);

  useEffect(() => {
    loadAccounts();
    const h = () => {
      loadAccounts();
      if (selectedId) loadLedger(selectedId);
    };
    window.addEventListener("workspace-changed", h);
    return () => window.removeEventListener("workspace-changed", h);
  }, [loadAccounts, loadLedger, selectedId]);

  useEffect(() => {
    if (selectedId) loadLedger(selectedId);
  }, [selectedId, loadLedger]);

  const refreshAll = useCallback(async () => {
    await loadAccounts();
    if (selectedId) await loadLedger(selectedId);
  }, [loadAccounts, loadLedger, selectedId]);

  // Toggle "favorite" for an account. Optimistic — update local state
  // immediately so the row jumps to the top of the sidebar with no perceived
  // network latency; roll back on failure.
  const toggleFavorite = useCallback(async (accountId, currentValue) => {
    const nextValue = !currentValue;
    setAccounts((prev) =>
      prev.map((a) => (a.id === accountId ? { ...a, is_favorite: nextValue } : a))
    );
    try {
      await api.patch(`/accounts/${accountId}`, { is_favorite: nextValue });
    } catch (e) {
      setAccounts((prev) =>
        prev.map((a) => (a.id === accountId ? { ...a, is_favorite: currentValue } : a))
      );
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  }, []);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? accounts.filter((a) => a.name.toLowerCase().includes(q)) : accounts;
    // Favorites float to the top; within each group we preserve the backend's
    // alphabetical sort so account positions stay stable across renders.
    return [...base].sort((x, y) => {
      const fx = x.is_favorite ? 1 : 0;
      const fy = y.is_favorite ? 1 : 0;
      if (fx !== fy) return fy - fx;
      return x.name.localeCompare(y.name);
    });
  }, [accounts, search]);

  const nameById = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a.name])),
    [accounts]
  );
  const idByName = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.name.toLowerCase(), a.id])),
    [accounts]
  );

  // Active accounts only — used for counterparty typeahead + resolution
  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.active !== false),
    [accounts]
  );

  const canEdit = level !== "view";

  // Global keyboard shortcuts scoped to the Entries page. All use Alt+letter
  // so they never collide with copy/cut/undo (⌘C/X/Z) or system browser keys.
  //   Alt + D → global search (also ⌘/Ctrl+K)
  //   Alt + S → focus sidebar account search
  //   Alt + Space → toggle "Keep" counterparty
  //   Alt + Z → open Zero-out dialog for the current account
  //   Alt + X → next account in sidebar
  //   Alt + C → previous account in sidebar
  // We match on `e.code` (physical key) NOT `e.key`, because on macOS Option +
  // a letter produces a special character (∂, ß, ç, …) instead of the letter.
  // Sidebar keyboard navigation — arrow keys move a highlight through the
  // filtered accounts list; Enter picks the highlighted row and closes search.
  const [sidebarActiveIdx, setSidebarActiveIdx] = useState(-1);
  useEffect(() => { setSidebarActiveIdx(-1); }, [search]);
  const scrollActiveIntoView = useCallback((idx) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid-idx="sidebar-row-idx-${idx}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const active = filteredAccounts.filter((a) => a.active !== false);
      const inactive = filteredAccounts.filter((a) => a.active === false);
      const visible = [...active, ...inactive];
      const code = e.code;
      if (code === "KeyS") {
        // sidebar account search
        e.preventDefault();
        const el = document.querySelector('[data-testid="sidebar-search"]');
        if (el) { el.focus(); el.select?.(); }
      } else if (code === "Space") {
        if (ledger && ledger.account.active !== false && canEdit) {
          e.preventDefault();
          setStickyCounterparty((v) => !v);
        }
      } else if (code === "KeyN") {
        // jump to counterparty input on the new-entry row. We call the ref's
        // imperative focus/select rather than a querySelector so it survives
        // component re-renders and reliably reaches the input inside the
        // combobox portal.
        if (ledger && ledger.account.active !== false && canEdit) {
          e.preventDefault();
          if (newCounterpartyRef.current?.focus) {
            newCounterpartyRef.current.focus();
            newCounterpartyRef.current.select?.();
          } else {
            const el = document.querySelector('[data-testid="new-counterparty"]');
            if (el) { el.focus(); el.select?.(); }
          }
        }
      } else if (code === "KeyZ") {
        // Zero-out helper. Move focus to the amount field. If a counterparty
        // is already picked and the current account has a non-zero balance,
        // pre-fill the amount that will zero-out the current account so a
        // single Enter continues the flow.
        if (ledger && ledger.account.active !== false && canEdit) {
          e.preventDefault();
          const closing = ledger.account.closing_balance || 0;
          const resolvedId = resolveCounterparty(newRow.counterparty);
          if (resolvedId && Math.abs(closing) > 0.001) {
            setNewRow((r) => ({ ...r, amount: String(-closing) }));
          }
          newAmountRef.current?.focus();
          newAmountRef.current?.select?.();
        }
      } else if (code === "KeyX" || code === "KeyC") {
        if (!visible.length) return;
        e.preventDefault();
        const idx = visible.findIndex((a) => a.id === selectedId);
        const next = code === "KeyX"
          ? visible[Math.min(visible.length - 1, idx + 1)]
          : visible[Math.max(0, idx - 1)];
        if (next) setSelectedId(next.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filteredAccounts, selectedId, ledger, canEdit, newRow.counterparty]);

  // Filtered entries: hide finalized by default, then apply search filter, then reverse (latest first)
  const filteredEntries = useMemo(() => {
    if (!ledger) return [];
    let list = ledger.entries;
    if (!showFinalized) {
      // Hide ONLY entries this account has finalized on its own side
      // (view_status === "finalized-here"). Entries locked because the
      // counterparty finalized their side ("locked-by-other") stay visible
      // in the active view — we just render them read-only with a lock icon.
      list = list.filter((e) => {
        const vs = e.view_status || (e.status === "finalized" ? "finalized-here" : "active");
        return vs !== "finalized-here";
      });
    }
    const q = filter.trim().toLowerCase();
    if (q) {
      list = list.filter((e) => {
        const counterName = (nameById[e.counterparty_id] || "").toLowerCase();
        const amtStr = String(Math.abs(e.amount));
        return (
          counterName.includes(q) ||
          (e.note || "").toLowerCase().includes(q) ||
          amtStr.includes(q)
        );
      });
    }
    // Reverse so latest appears at top
    return [...list].reverse();
  }, [ledger, filter, nameById, showFinalized]);

  const finalizedCount = useMemo(() => {
    if (!ledger) return 0;
    // Count only THIS account's own finalizations for the "Show older" chip.
    if (ledger.include_finalized) {
      return (ledger.entries || []).filter((e) => {
        const vs = e.view_status || (e.status === "finalized" ? "finalized-here" : "active");
        return vs === "finalized-here";
      }).length;
    }
    return ledger.finalized_count_hidden || 0;
  }, [ledger]);

  const onToggleShowFinalized = async () => {
    const next = !showFinalized;
    setShowFinalized(next);
    // Refetch — if the current payload already matches, this is a no-op cost-wise.
    if (selectedId) await loadLedger(selectedId, { includeFinalized: next });
  };

  const toggleTally = async (entryId, currentTallied) => {
    if (!canEdit) return;
    try {
      await api.patch(`/entries/${entryId}/tally`, { tallied: !currentTallied });
      setLedger((l) =>
        l
          ? {
              ...l,
              entries: l.entries.map((e) =>
                e.id === entryId ? { ...e, tallied: !currentTallied } : e
              ),
            }
          : l
      );
    } catch (e) {
      setMsg({ kind: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const resolveCounterparty = (input) => {
    const q = input.trim().toLowerCase();
    if (!q) return null;
    if (idByName[q]) return idByName[q];
    // Prefix match — active accounts only
    const matches = activeAccounts.filter(
      (a) => a.id !== selectedId && a.name.toLowerCase().startsWith(q)
    );
    if (matches.length === 1) return matches[0].id;
    // Substring match — active accounts only
    const contains = activeAccounts.filter(
      (a) => a.id !== selectedId && a.name.toLowerCase().includes(q)
    );
    if (contains.length === 1) return contains[0].id;
    return null;
  };

  const submitNewRow = async () => {
    if (!selectedId) return;
    setMsg(null);
    const counterId = resolveCounterparty(newRow.counterparty);
    if (!counterId) {
      setMsg({ kind: "err", text: `Counterparty "${newRow.counterparty}" not found.` });
      newCounterpartyRef.current?.focus();
      return;
    }
    // Resolve a possible formula (`=236*4`) before validating. This matches
    // the field's visual behaviour on blur and lets a user Alt+N → type
    // formula → Enter without first collapsing it manually.
    const amt = evalFormula(newRow.amount);
    if (amt == null || amt === 0) {
      setMsg({ kind: "err", text: "Enter a non-zero amount." });
      newAmountRef.current?.focus();
      return;
    }
    // Sign convention (user-friendly):
    //   Positive number   → CREDIT for the selected account: counterparty → selected
    //   Negative number   → DEBIT  for the selected account: selected → counterparty
    // The backend `amount` is always stored as a positive absolute value.
    const posted = amt >= 0
      ? { from_account_id: counterId, to_account_id: selectedId, amount: amt }
      : { from_account_id: selectedId, to_account_id: counterId, amount: -amt };
    // Optimistically append so the input feels instant. On the wire error we'll
    // roll back below.
    const nowIso = new Date().toISOString();
    const tempId = `__temp_${Date.now()}`;
    const change = amt; // signed change on the selected account's balance
    setLedger((l) => {
      if (!l) return l;
      const prevRunning = l.entries.length
        ? l.entries[l.entries.length - 1].running_balance
        : (l.account.active_opening_balance ?? l.account.opening_balance ?? 0);
      const optimistic = {
        id: tempId,
        from_account_id: posted.from_account_id,
        to_account_id: posted.to_account_id,
        counterparty_id: counterId,
        amount: posted.amount,
        change,
        running_balance: prevRunning + change,
        note: newRow.note || "",
        status: "active",
        batch_id: null,
        tallied: false,
        modified: false,
        created_at: nowIso,
        updated_at: nowIso,
        created_by_name: "",
        updated_by_name: "",
        tallied_by_name: "",
        __optimistic: true,
      };
      return {
        ...l,
        entries: [...l.entries, optimistic],
        account: { ...l.account, closing_balance: (l.account.closing_balance ?? 0) + change },
      };
    });
    setNewRow((r) => ({ counterparty: stickyCounterparty ? r.counterparty : "", amount: "", note: "" }));
    // When Keep is on the counterparty is locked in — jump straight to the
    // amount field so the dropdown doesn't reopen and cover half the screen.
    // Otherwise (fresh entry), focus counterparty so the user starts typing.
    if (stickyCounterparty) {
      newAmountRef.current?.focus();
    } else {
      newCounterpartyRef.current?.focus();
    }
    try {
      const { data } = await api.post("/entries", { ...posted, note: newRow.note });
      // Replace optimistic id with the real one — no full reload.
      setLedger((l) => {
        if (!l) return l;
        return { ...l, entries: l.entries.map((e) => (e.id === tempId ? { ...e, id: data.id, __optimistic: false } : e)) };
      });
      pushRecentCounterparty(counterId);
      // Sidebar balances (both this account + counterparty) refresh in the
      // background so the picker stays accurate; UI stays snappy meanwhile.
      loadAccounts();
    } catch (err) {
      // Roll back the optimistic append.
      setLedger((l) => {
        if (!l) return l;
        return {
          ...l,
          entries: l.entries.filter((e) => e.id !== tempId),
          account: { ...l.account, closing_balance: (l.account.closing_balance ?? 0) - change },
        };
      });
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(err?.response?.data?.detail) || err.message || "Failed to create entry",
      });
    }
  };

  const startEdit = useCallback((e) => {
    const vs = e.view_status || (e.status === "finalized" ? "finalized-here" : "active");
    if (vs !== "active" || !canEdit) return;
    setEditingId(e.id);
    setEditRow({
      counterparty: nameById[e.counterparty_id] || "",
      amount: String(e.change),
      note: e.note || "",
    });
  }, [canEdit, nameById]);

  const clickTimerRef = useRef(null);
  const handleRowClick = useCallback((e) => {
    // Reject when the entry is locked from this account's view (either
    // finalized-here or locked-by-counterparty). Both are read-only.
    const vs = e.view_status || (e.status === "finalized" ? "finalized-here" : "active");
    if (vs !== "active" || !canEdit) return;
    if (editingId) return; // already editing something
    clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      startEdit(e);
    }, 260);
  }, [canEdit, editingId, startEdit]);
  const handleRowDoubleClick = useCallback((e) => {
    clearTimeout(clickTimerRef.current);
    toggleTally(e.id, e.tallied);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditRow(null);
  }, []);

  const saveEdit = async (entry) => {
    setMsg(null);
    const counterId = resolveCounterparty(editRow.counterparty);
    if (!counterId) {
      setMsg({ kind: "err", text: `Counterparty "${editRow.counterparty}" not found.` });
      return;
    }
    const changeVal = evalFormula(editRow.amount);
    if (changeVal == null || changeVal === 0) {
      setMsg({ kind: "err", text: "Enter a non-zero amount." });
      return;
    }
    // Convert "change from selected account POV" back to signed entry amount.
    // If selected is "from": change = -amount → amount = -change
    // If selected is "to":   change =  amount → amount =  change
    const isFrom = entry.from_account_id === selectedId;
    const amount = isFrom ? -changeVal : changeVal;
    const fromId = isFrom ? selectedId : counterId;
    const toId = isFrom ? counterId : selectedId;
    try {
      await api.patch(`/entries/${entry.id}`, {
        from_account_id: fromId,
        to_account_id: toId,
        amount,
        note: editRow.note,
      });
      cancelEdit();
      await refreshAll();
    } catch (err) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  const [finalizeScope, setFinalizeScope] = useState(null); // "account" | "workspace" | null

  const doFinalize = async (scope) => {
    try {
      const params = scope === "account" && selectedId ? { account_id: selectedId } : {};
      const { data } = await api.post("/entries/finalize", null, { params });
      const where = data.scope === "account"
        ? ` on ${data.scope_account_name || "this account"}`
        : " across the whole workspace";
      const detail = data.moved_to_older_only > 0
        ? ` (${data.newly_locked} newly locked, ${data.moved_to_older_only} moved to older only)`
        : "";
      setMsg({ kind: "ok", text: `Finalized ${data.finalized_count} entries${where}${detail}.` });
      setFinalizeScope(null);
      refreshAll();
    } catch (e) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const repairLegacyFinalized = async () => {
    if (!window.confirm(
      "Scan for backfilled 'finalized' entries that were partially broken by a prior 'Finalize this account' click, and restore them to be hidden on both sides. Safe to run repeatedly — proceed?"
    )) return;
    try {
      const { data } = await api.post("/entries/repair-legacy-finalized");
      setMsg({ kind: "ok", text: data.message || `Repaired ${data.repaired} entries.` });
      refreshAll();
    } catch (e) {
      setMsg({
        kind: "err",
        text: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const downloadXlsx = async (scope = "account") => {
    // scope: "account" (default) exports just this ledger; "all" dumps the whole workspace.
    try {
      const params = { status: "all" };
      if (scope === "account" && selectedId) params.account_id = selectedId;
      const resp = await api.get("/export/entries.xlsx", {
        params,
        responseType: "blob",
      });
      const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"] || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fallback = scope === "account"
        ? `ledger-${ledger?.account?.name || "account"}.xlsx`
        : "entries-all.xlsx";
      const filename = m ? decodeURIComponent(m[1]) : fallback;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setMsg({ kind: "err", text: `Export failed: ${err?.response?.statusText || err.message}` });
    }
  };

  // Memoise the entry rows JSX so typing into the new-entry inputs above
  // doesn't force React to rebuild every ledger row on every keystroke.
  //
  // Row-state classes intentionally use LEFT-BORDER STRIPES + brighter tints
  // instead of pastel fills — the pastel tokens vanish against the dark
  // charcoal theme. The left stripe (4px inset shadow) reads at a glance from
  // the sidebar side without dominating the row, works identically in light &
  // dark themes, and keeps colour semantics consistent (blue=modified,
  // green=tallied, gold=editing, stone=finalized/older).
  const rowBg = (e, idx) => {
    if (editingId === e.id)
      return "row-state row-state--editing";
    if (e.tallied)
      return "row-state row-state--tallied";
    if (e.modified)
      return "row-state row-state--modified";
    // Zebra: even rows get a whisper of contrast so long ledgers stay scannable.
    return idx % 2 === 0 ? "row-zebra-even" : "row-zebra-odd";
  };
  const activeAccountsForEdit = useMemo(
    () => activeAccounts.filter((a) => a.id !== selectedId),
    [activeAccounts, selectedId]
  );
  const renderedRows = useMemo(() => {
    let firstFinalizedInjected = false;
    return filteredEntries.map((e, idx) => {
      const isEditing = editingId === e.id;
      // `view_status` (set by backend from THIS account's perspective):
      //   "active"           → editable, live entry.
      //   "finalized-here"   → this account has finalized it — lives in "older".
      //   "locked-by-other"  → the counterparty has finalized their side; still
      //                        visible in this account's active view but with a
      //                        lock icon and NOT editable.
      const vs = e.view_status || (e.status === "finalized" ? "finalized-here" : "active");
      const isLocked = vs !== "active";
      const lockedByOther = vs === "locked-by-other";
      const aam = e.change < 0 ? Math.abs(e.change) : null;
      const jama = e.change > 0 ? e.change : null;

      // Older-entries separator band — only shown when Show older is on. We
      // sniff the FIRST row whose view_status is "finalized-here" and inject a
      // full-width divider before it.
      const injectSeparator =
        showFinalized &&
        !firstFinalizedInjected &&
        vs === "finalized-here";
      if (injectSeparator) firstFinalizedInjected = true;

      const rowEl = isEditing ? (
        <InlineEditRow
          key={e.id}
          entry={e}
          row={editRow}
          setRow={setEditRow}
          onSave={() => saveEdit(e)}
          onCancel={cancelEdit}
          options={activeAccountsForEdit}
        />
      ) : (
        <div
          key={e.id}
          onClick={() => handleRowClick(e)}
          onDoubleClick={(ev) => {
            ev.stopPropagation();
            handleRowDoubleClick(e);
          }}
          className={`grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center transition-colors ${isLocked || !canEdit ? "cursor-default" : "cursor-text"} ${rowBg(e, idx)} ${vs === "finalized-here" ? "row-finalized" : ""}`}
          data-testid={`ledger-row-${e.id}`}
          title={
            vs === "finalized-here"
              ? "Finalized on this account — read-only"
              : lockedByOther
                ? "Counterparty finalized their side — read-only"
                : "Click to edit, double-click to tally"
          }
        >
          <div className="col-span-2 text-xs text-stone-600 flex items-center gap-1">
            {new Date(e.created_at).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            })}
            {vs === "finalized-here" && <Lock size={10} className="text-stone-400" />}
            {lockedByOther && (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider px-1 py-0 rounded-full bg-amber-100 text-amber-800 font-semibold"
                title="Counterparty finalized their side of this entry — read-only from here too"
              >
                <Lock size={8} strokeWidth={2.5} />
              </span>
            )}
          </div>
          <div className="col-span-2 truncate">
            <span className="font-medium">{nameById[e.counterparty_id] || "?"}</span>
          </div>
          <div className="col-span-2 truncate text-sm text-stone-500" title={e.note || ""}>
            {e.note || <span className="text-stone-300">—</span>}
          </div>
          <div className="col-span-2 text-right font-serif text-[26px] leading-none tabular-nums text-emerald-700">
            {jama ? num(jama) : ""}
          </div>
          <div className="col-span-1 text-right font-serif text-[26px] leading-none tabular-nums text-rose-700">
            {aam ? `−${num(aam)}` : ""}
          </div>
          <div
            className={`col-span-2 text-right font-serif text-[26px] leading-none tabular-nums ${
              e.running_balance > 0
                ? "text-emerald-700"
                : e.running_balance < 0
                  ? "text-rose-700"
                  : "text-stone-600"
            }`}
          >
            {num(e.running_balance)}
          </div>
          <div className="col-span-1 flex items-center justify-center gap-1">
            <span
              className="w-3.5 h-3.5 inline-flex items-center justify-center flex-shrink-0"
              aria-hidden={!e.tallied}
            >
              {e.tallied && (
                <Check size={14} className="text-emerald-700" strokeWidth={3} />
              )}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  onClick={(ev) => ev.stopPropagation()}
                  className="w-5 h-5 rounded-full hover:bg-white inline-flex items-center justify-center text-stone-400 flex-shrink-0"
                  data-testid={`audit-${e.id}`}
                >
                  <Info size={11} strokeWidth={1.5} />
                </span>
              </TooltipTrigger>
              <TooltipContent className="glass-strong rounded-xl text-xs max-w-[240px] p-3">
                <AuditInfo entry={e} />
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      );

      if (injectSeparator) {
        return (
          <div key={`sep-${e.id}`}>
            <div
              className="older-separator"
              data-testid="older-entries-separator"
              role="separator"
              aria-label="Older finalized entries below"
            >
              <span className="older-separator__line" />
              <span className="older-separator__label">
                <Lock size={10} />
                OLDER · FINALIZED · {finalizedCount.toLocaleString()} entries
              </span>
              <span className="older-separator__line" />
            </div>
            {rowEl}
          </div>
        );
      }
      return rowEl;
    });

  }, [filteredEntries, editingId, editRow, activeAccountsForEdit, nameById, canEdit, handleRowClick, handleRowDoubleClick, cancelEdit, showFinalized, finalizedCount]);

  return (
    <div className="space-y-3" data-testid="entries-page">
      {msg && (
        <div
          className={`app-toast rounded-2xl px-4 py-3 text-sm ${
            msg.kind === "ok" ? "app-toast--ok" : "app-toast--err"
          }`}
          data-testid={`entries-toast-${msg.kind}`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-2">
          <GlassCard className="p-0 overflow-hidden">
            <div className="p-4 border-b border-stone-200/60">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="sidebar-search"
                  type="text"
                  placeholder="Search accounts… (Alt+S)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    // Ordered candidate list mirrors the render order below:
                    // active accounts first, then inactive.
                    const activeList = filteredAccounts.filter((a) => a.active !== false);
                    const inactiveList = filteredAccounts.filter((a) => a.active === false);
                    const ordered = [...activeList, ...inactiveList];
                    if (e.key === "ArrowDown") {
                      if (!ordered.length) return;
                      e.preventDefault();
                      setSidebarActiveIdx((i) => {
                        const next = Math.min(ordered.length - 1, i + 1);
                        scrollActiveIntoView(next);
                        return next;
                      });
                    } else if (e.key === "ArrowUp") {
                      if (!ordered.length) return;
                      e.preventDefault();
                      setSidebarActiveIdx((i) => {
                        const next = Math.max(0, i - 1);
                        scrollActiveIntoView(next);
                        return next;
                      });
                    } else if (e.key === "Enter") {
                      // Prefer highlighted row → else first active → else first any.
                      const pick =
                        (sidebarActiveIdx >= 0 && ordered[sidebarActiveIdx]) ||
                        activeList[0] ||
                        ordered[0];
                      if (pick) {
                        e.preventDefault();
                        setSelectedId(pick.id);
                        setSearch("");
                        setSidebarActiveIdx(-1);
                        e.currentTarget.blur();
                      }
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setSearch("");
                      setSidebarActiveIdx(-1);
                      e.currentTarget.blur();
                    }
                  }}
                  className="w-full pl-8 pr-3 py-2 rounded-full bg-white/80 border border-white/90 text-sm focus:bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
                />
              </div>
            </div>
            <div className="max-h-[68vh] overflow-y-auto">
              {filteredAccounts.length === 0 && (
                <div className="text-center text-sm text-stone-500 py-8">No accounts</div>
              )}
              {(() => {
                const activeList = filteredAccounts.filter((a) => a.active !== false);
                const inactiveList = filteredAccounts.filter((a) => a.active === false);
                const ordered = [...activeList, ...inactiveList];
                const renderRow = (a) => {
                  const isInactive = a.active === false;
                  const orderedIdx = ordered.indexOf(a);
                  const isKbHighlight = orderedIdx === sidebarActiveIdx;
                  return (
                    <div
                      key={a.id}
                      className={`group w-full flex items-center border-l-2 transition-colors ${
                        selectedId === a.id
                          ? "bg-white/70 border-[color:var(--gold)]"
                          : isKbHighlight
                            ? "bg-[color:var(--gold-soft)] border-[color:var(--gold)]"
                            : "border-transparent hover:bg-white/40"
                      } ${isInactive ? "opacity-80" : ""}`}
                    >
                      <button
                        onClick={() => {
                          setSelectedId(a.id);
                          if (search) setSearch("");
                          setSidebarActiveIdx(-1);
                        }}
                        data-testid={`sidebar-account-${a.id}`}
                        data-testid-idx={`sidebar-row-idx-${orderedIdx}`}
                        {...(isKbHighlight ? { "data-kb-active": "true" } : {})}
                        className="flex-1 min-w-0 text-left pl-3 pr-1 py-2 flex items-center gap-2"
                        title={isInactive ? "Inactive account — read-only" : (a.balance > 0 ? `Credit ${num(a.balance)}` : a.balance < 0 ? `Debit ${num(a.balance)}` : "Zero")}
                      >
                        <span
                          aria-hidden
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            a.balance > 0
                              ? "bg-emerald-500"
                              : a.balance < 0
                                ? "bg-rose-500"
                                : "bg-stone-400"
                          }`}
                        />
                        <span className={`text-sm font-medium truncate ${isInactive ? "text-stone-400 dark:text-stone-500 line-through decoration-stone-500/40 decoration-1" : ""}`}>
                          {a.name}
                        </span>
                        {isInactive && (
                          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 flex-shrink-0 ml-auto" data-testid={`sidebar-inactive-${a.id}`}>
                            Off
                          </span>
                        )}
                      </button>
                      {/* Favorite toggle — sits OUTSIDE the row's main button so
                          clicking the star doesn't navigate to the account. */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(a.id, !!a.is_favorite);
                        }}
                        title={a.is_favorite ? "Unpin from top" : "Pin to top"}
                        aria-label={a.is_favorite ? `Unpin ${a.name}` : `Pin ${a.name} to top`}
                        data-testid={`sidebar-fav-${a.id}`}
                        className={`flex-shrink-0 px-2 py-2 transition-opacity ${
                          a.is_favorite
                            ? "opacity-100 text-[color:var(--gold)]"
                            : "opacity-0 group-hover:opacity-60 hover:!opacity-100 text-stone-400"
                        }`}
                      >
                        <Star
                          size={13}
                          fill={a.is_favorite ? "currentColor" : "none"}
                          strokeWidth={a.is_favorite ? 1.5 : 2}
                        />
                      </button>
                    </div>
                  );
                };
                return (
                  <>
                    {(() => {
                      const pinned = activeList.filter((a) => a.is_favorite);
                      const rest = activeList.filter((a) => !a.is_favorite);
                      return (
                        <>
                          {pinned.length > 0 && (
                            <>
                              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-[color:var(--gold-deep)] bg-transparent flex items-center gap-1.5" data-testid="sidebar-section-pinned">
                                <Star size={10} fill="currentColor" strokeWidth={1.5} />
                                Pinned · {pinned.length}
                              </div>
                              {pinned.map(renderRow)}
                            </>
                          )}
                          {rest.length > 0 && (
                            <>
                              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-stone-500 bg-transparent" data-testid="sidebar-section-active">
                                {pinned.length > 0 ? "All accounts" : "Active"} · {rest.length}
                              </div>
                              {rest.map(renderRow)}
                            </>
                          )}
                        </>
                      );
                    })()}
                    {inactiveList.length > 0 && (
                      <>
                        <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-wider text-rose-600 border-t border-stone-200/60 mt-1 flex items-center gap-2 bg-transparent" data-testid="sidebar-section-inactive">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block" />
                          Inactive · {inactiveList.length}
                        </div>
                        {inactiveList.map(renderRow)}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </GlassCard>
        </div>

        {/* Ledger */}
        <div className="col-span-12 lg:col-span-10 space-y-2 relative">
          {/* Loading overlay — covers the whole entries section while a new
              account is being loaded. Sits above the previous ledger so the
              user sees a clear "working" state instead of a frozen table. */}
          {busy && ledger && (
            <div
              className="absolute inset-0 z-30 flex items-center justify-center rounded-3xl bg-white/60 backdrop-blur-[2px] pointer-events-none"
              data-testid="ledger-loading-overlay"
            >
              <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/95 shadow-lg border border-stone-200">
                <Spinner size={16} />
                <span className="text-sm font-serif text-stone-700">Loading ledger…</span>
              </div>
            </div>
          )}
          {ledger ? (
            <>
              {/* Table */}
              <GlassCard className="p-0 overflow-hidden border border-stone-200/70">
                {/* One-line account header — sits FLUSH on top of the table so
                    the maximum number of entries stays in the viewport. */}
                {(() => {
                  const opening = showFinalized
                    ? ledger.account.opening_balance
                    : (ledger.account.active_opening_balance ?? ledger.account.opening_balance);
                  const closing = ledger.account.closing_balance;
                  const diff = closing - opening;
                  const tone = (v) =>
                    v > 0 ? "text-emerald-700" : v < 0 ? "text-rose-700" : "text-stone-500";
                  return (
                    <div
                      className="flex items-center justify-between gap-3 flex-wrap px-4 py-2 border-b border-stone-200/60 bg-[color:var(--gold-soft)]/25"
                      data-testid="account-header-inline"
                    >
                      <div className="flex items-baseline gap-3 flex-wrap min-w-0">
                        <span className="font-serif text-xl leading-tight truncate max-w-[240px]" data-testid="account-name">
                          {ledger.account.name}
                        </span>
                        {ledger.account.active === false && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-sans" data-testid="account-inactive-badge">
                            Inactive
                          </span>
                        )}
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">Op.</span>
                        <span className={`font-serif text-[26px] leading-none tabular-nums ${tone(opening)}`} data-testid="account-opening">
                          {num(opening)}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">Cl.</span>
                        <span className={`font-serif text-[26px] leading-none tabular-nums ${tone(closing)}`} data-testid="account-closing">
                          {num(closing)}
                        </span>
                        <span
                          className="text-[10px] uppercase tracking-wider text-stone-500"
                          title="Closing − Opening (net change in this view)"
                        >Δ</span>
                        <span className={`font-serif text-[26px] leading-none tabular-nums ${tone(diff)}`} data-testid="account-diff">
                          {diff > 0 ? "+" : ""}{num(diff)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-nowrap">
                        <div className="relative">
                          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                          <input
                            type="text"
                            placeholder="Filter…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            data-testid="entries-filter"
                            className="w-32 pl-6 pr-2 py-1 rounded-full bg-white/80 border border-white/90 text-[11px] focus:bg-white outline-none"
                          />
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="btn-ghost btn-ghost-sm flex items-center gap-1"
                            data-testid="entries-export-btn"
                            title="Export ledger"
                          >
                            <Download size={11} /> Export
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="rounded-xl min-w-[220px]" align="end">
                            <DropdownMenuLabel className="label-tiny">Export as .xlsx</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => downloadXlsx("account")}
                              data-testid="export-this-account"
                            >
                              <Download size={12} className="mr-2" />
                              This account only
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => downloadXlsx("all")}
                              data-testid="export-all-entries"
                            >
                              <Download size={12} className="mr-2" />
                              All workspace entries
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {level === "owner" && (
                          <>
                            <button
                              className="btn-ghost btn-ghost-sm flex items-center gap-1 text-rose-600"
                              onClick={() => setFinalizeScope("account")}
                              data-testid="finalize-this-account-btn"
                              title={`Lock all active entries on "${ledger.account.name}" only`}
                            >
                              <Lock size={11} /> Finalize this
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                className="btn-ghost btn-ghost-sm flex items-center gap-1 text-rose-600"
                                data-testid="finalize-more-menu"
                                title="More finalize / repair options"
                              >
                                <MoreHorizontal size={11} /> More…
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="rounded-xl min-w-[260px]" align="end">
                                <DropdownMenuLabel className="label-tiny">Maintenance</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={repairLegacyFinalized}
                                  disabled={busy}
                                  data-testid="repair-legacy-btn"
                                >
                                  <Zap size={12} className="mr-2" />
                                  Repair legacy entries
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="label-tiny text-rose-600">
                                  Danger zone
                                </DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => setFinalizeScope("workspace")}
                                  data-testid="finalize-workspace-btn"
                                  className="text-rose-600"
                                >
                                  <Lock size={12} className="mr-2" />
                                  Finalize entire workspace
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                        {finalizedCount > 0 && (
                          <>
                            <button
                              onClick={onToggleShowFinalized}
                              disabled={busy}
                              className="btn-ghost btn-ghost-sm flex items-center gap-1 disabled:opacity-60"
                              data-testid="toggle-older-entries"
                              title={showFinalized ? "Hide finalized entries" : `Show ${finalizedCount} finalized entries`}
                            >
                              {busy ? <Spinner size={11} /> : (showFinalized ? <Eye size={11} /> : <EyeOff size={11} />)}
                              {showFinalized ? `Hide older (${finalizedCount})` : `Show older (${finalizedCount})`}
                            </button>
                            <button
                              onClick={() => setFinalizeLogOpen(true)}
                              className="btn-ghost btn-ghost-sm flex items-center gap-1"
                              data-testid="finalize-log-btn"
                              title="See who and how these entries got finalized"
                            >
                              <Info size={11} /> Log
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {/* Inline new-entry row FIRST (hidden when the current account is inactive) */}
                {canEdit && ledger.account.active !== false && (() => {
                  const resolvedCounterId = resolveCounterparty(newRow.counterparty);
                  const resolvedCounter = resolvedCounterId
                    ? accounts.find((a) => a.id === resolvedCounterId)
                    : null;
                  const displayBal = resolvedCounter ? resolvedCounter.balance : 0;
                  // Unified input classes so all four fields have exactly the
                  // same height, radius, padding and border weight. Bumped
                  // font-size so the numbers actually READ at 100% zoom.
                  const inputBase = "w-full h-10 px-3 rounded-lg border border-stone-200 bg-white outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 text-base";
                  return (
                    <div
                      className="grid grid-cols-12 gap-2 px-3 py-2 items-center new-entry-tray"
                      data-testid="new-entry-row"
                    >
                      {/* Keep toggle + Counterparty balance readonly — same
                          height as the other fields, no vertical labels. */}
                      <div className="col-span-3 flex items-center gap-1.5">
                        <label
                          className={`flex-shrink-0 flex items-center gap-1 h-9 px-2 rounded-lg border cursor-pointer select-none transition-colors ${
                            stickyCounterparty
                              ? "border-[color:var(--gold)] bg-[color:var(--gold-soft)]/70 text-[color:var(--gold-deep)]"
                              : "border-stone-200 bg-white/70 text-stone-500 hover:border-[color:var(--gold)]"
                          }`}
                          title="Keep the counterparty after posting so you can log several entries for the same person in a row. Alt+Space toggles."
                          data-testid="sticky-counterparty-label"
                        >
                          <input
                            type="checkbox"
                            checked={stickyCounterparty}
                            onChange={(e) => setStickyCounterparty(e.target.checked)}
                            className="w-3 h-3 rounded cursor-pointer"
                            data-testid="sticky-counterparty-toggle"
                          />
                          <span className="text-[10px] uppercase tracking-wider font-semibold">Keep</span>
                        </label>
                        <input
                          type="text"
                          readOnly
                          tabIndex={-1}
                          value={resolvedCounter ? num(displayBal) : ""}
                          placeholder="C-party bal."
                          title={resolvedCounter ? `${resolvedCounter.name} current balance` : "Pick a counterparty to see their live balance"}
                          data-testid="counterparty-balance-input"
                          className={`flex-1 min-w-0 h-12 px-3 rounded-lg border border-stone-200/70 bg-stone-50/70 font-serif text-right cursor-default select-all tabular-nums text-[26px] leading-none ${
                            displayBal > 0
                              ? "text-emerald-700"
                              : displayBal < 0
                                ? "text-rose-700"
                                : "text-stone-400"
                          }`}
                        />
                      </div>
                      <div className="col-span-3">
                        <CounterpartyCombobox
                          ref={newCounterpartyRef}
                          value={newRow.counterparty}
                          onChange={(v) => setNewRow((r) => ({ ...r, counterparty: v }))}
                          options={activeAccounts.filter((a) => a.id !== selectedId)}
                          onSelect={(o) => {
                            setNewRow((r) => ({ ...r, counterparty: o.name }));
                            newAmountRef.current?.focus();
                          }}
                          onSubmit={() => newAmountRef.current?.focus()}
                          placeholder="Counterparty…"
                          testId="new-counterparty"
                          inputClassName={inputBase}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          ref={newAmountRef}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="Enter to auto-fill · +credit / −debit · =236*4 OK"
                          value={newRow.amount}
                          onChange={(e) => setNewRow((r) => ({ ...r, amount: e.target.value }))}
                          onBlur={() => {
                            // Collapse formulas (`=236*4`) into their numeric
                            // result when the user tabs/clicks away. Leave
                            // plain numeric text and empty strings alone.
                            const s = String(newRow.amount || "").trim();
                            if (!s.startsWith("=")) return;
                            const v = evalFormula(s);
                            if (v != null) setNewRow((r) => ({ ...r, amount: String(v) }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              // Resolve formula first so Enter-to-advance uses
                              // the computed number, not the raw expression.
                              const raw = String(newRow.amount || "").trim();
                              let resolved = raw;
                              if (raw.startsWith("=")) {
                                const v = evalFormula(raw);
                                if (v != null) {
                                  resolved = String(v);
                                  setNewRow((r) => ({ ...r, amount: resolved }));
                                }
                              }
                              const isEmpty = !resolved || resolved === "";
                              const closing = ledger?.account?.closing_balance || 0;
                              // If the amount is empty AND we have a resolved
                              // counterparty AND the current account has a
                              // non-zero balance, first Enter auto-fills the
                              // exact zero-out amount but STAYS on this field
                              // so you can eyeball it. Second Enter advances
                              // to Note. If a value is already present, jump
                              // straight to Note.
                              if (isEmpty && resolvedCounter && Math.abs(closing) > 0.001) {
                                // Sign convention: +N = credit (money in) → current +N,
                                // −N = debit (money out) → current −N. To zero out, current
                                // change must equal −closing, so amount = −closing.
                                const filled = String(-closing);
                                setNewRow((r) => ({ ...r, amount: filled }));
                                return; // stay on Amount — user hits Enter again to continue
                              }
                              newNoteRef.current?.focus();
                            }
                          }}
                          title="Positive = money coming IN (credit). Negative = money going OUT (debit). Prefix with = for a formula, e.g. =236*4. Press Enter on an empty field to auto-fill the amount that zero-outs this account."
                          data-testid="new-amount"
                          className={`${inputBase} font-serif text-right text-[26px] leading-none tabular-nums ${
                            (evalFormula(newRow.amount) ?? 0) > 0
                              ? "text-emerald-700"
                              : (evalFormula(newRow.amount) ?? 0) < 0
                                ? "text-rose-700"
                                : ""
                          }`}
                        />
                      </div>
                      <div className="col-span-3">
                        <input
                          ref={newNoteRef}
                          type="text"
                          placeholder="Note (optional)…"
                          value={newRow.note}
                          onChange={(e) => setNewRow((r) => ({ ...r, note: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              submitNewRow();
                            }
                          }}
                          data-testid="new-note"
                          className={inputBase}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={submitNewRow}
                          className="btn-gold h-9 px-3 text-xs w-full"
                          data-testid="new-post-btn"
                        >
                          Post
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Recent counterparties chips — one-click autofill of last 5 used */}
                {canEdit && ledger.account.active !== false && recentCounterparties.length > 0 && (
                  <div
                    className="flex items-center gap-2 px-4 py-2 border-b border-stone-200/60 bg-[color:var(--gold-soft)]/20 flex-wrap"
                    data-testid="recent-chips"
                  >
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 flex-shrink-0">
                      Recent
                    </span>
                    {recentCounterparties.map((cpId) => {
                      const a = accounts.find((x) => x.id === cpId);
                      if (!a || a.active === false || a.id === selectedId) return null;
                      return (
                        <button
                          key={cpId}
                          onClick={() => {
                            setNewRow((r) => ({ ...r, counterparty: a.name }));
                            newAmountRef.current?.focus();
                          }}
                          className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 border border-stone-200 hover:border-[color:var(--gold)] hover:bg-white text-sm transition-colors"
                          data-testid={`recent-chip-${cpId}`}
                          title={`Use ${a.name} as counterparty`}
                        >
                          <span className="font-medium truncate max-w-[140px]">{a.name}</span>
                          <span
                            className={`font-serif text-base tabular-nums ${
                              a.balance > 0 ? "text-emerald-700" : a.balance < 0 ? "text-rose-700" : "text-stone-500"
                            }`}
                          >
                            {num(a.balance)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Data rows */}
                <div className="max-h-[74vh] overflow-y-auto divide-y divide-stone-200/60 ledger-table-body">
                  {/* Column headers — MUST live inside the scroll container so
                      the reserved scrollbar column doesn't shift the data rows
                      left of the headers. `sticky top-0` keeps them pinned at
                      the top while the ledger scrolls beneath. */}
                  <div className="ledger-header grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase tracking-wider font-semibold bg-[color:var(--gold-soft)]/70 text-stone-700 border-b border-stone-200/70 sticky top-0 z-10">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-2">Name</div>
                    <div className="col-span-2">Note</div>
                    <div className="col-span-2 text-right text-emerald-700">
                      <ArrowUp size={12} className="inline" /> Credit
                    </div>
                    <div className="col-span-1 text-right text-rose-700">
                      <ArrowDown size={12} className="inline" /> Debit
                    </div>
                    <div className="col-span-2 text-right">Balance</div>
                    <div className="col-span-1 text-center">Ch</div>
                  </div>
                  {filteredEntries.length === 0 ? (
                    <div className="px-6 py-10 text-center text-stone-500 text-sm">
                      {filter
                        ? "No entries match your filter."
                        : showFinalized
                          ? "No entries for this account yet."
                          : finalizedCount > 0
                            ? "No active entries. Click 'Show older entries' below to see finalized ones."
                            : "No entries for this account yet."}
                    </div>
                  ) : (
                    <TooltipProvider delayDuration={200}>
                      {renderedRows}
                    </TooltipProvider>
                  )}
                </div>
              </GlassCard>
            </>
          ) : busy ? (
            <GlassCard>
              <SpinnerBlock label="Loading ledger…" />
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-20 text-stone-500">
              Choose an account from the left to view its ledger.
            </GlassCard>
          )}
        </div>
      </div>

      {transferOpen && ledger && (
        <TransferFullBalanceDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          onSaved={refreshAll}
          account={ledger.account}
          accounts={accounts}
          selectedId={selectedId}
        />
      )}

      {finalizeLogOpen && ledger && (
        <FinalizeLogDialog
          open={finalizeLogOpen}
          onClose={() => setFinalizeLogOpen(false)}
          accountId={selectedId}
          accountName={ledger.account.name}
        />
      )}


      <AlertDialog open={!!finalizeScope} onOpenChange={(v) => !v && setFinalizeScope(null)}>
        <AlertDialogContent className="glass-strong rounded-3xl">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center mb-2">
              <ShieldAlert size={22} className="text-rose-600" />
            </div>
            <AlertDialogTitle className="font-serif text-2xl">
              {finalizeScope === "account"
                ? `Finalize entries on ${ledger?.account.name}?`
                : "Finalize entire workspace?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {finalizeScope === "account" ? (
                <>
                  This locks every active entry that touches{" "}
                  <b>{ledger?.account.name}</b> — no other account is affected.
                  Locked entries can&rsquo;t be edited or deleted, but stay
                  visible under &ldquo;Show older&rdquo;.
                </>
              ) : (
                <>
                  This locks <b>every active entry in your entire workspace</b>{" "}
                  — every account, not just the one you&rsquo;re viewing. Use
                  with care: locked entries can&rsquo;t be edited or deleted
                  afterwards.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doFinalize(finalizeScope)}
              className="btn-gold"
              data-testid="finalize-confirm-btn"
            >
              {finalizeScope === "account" ? "Yes, finalize this account" : "Yes, finalize workspace"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InlineEditRow({ entry, row, setRow, onSave, onCancel, options }) {
  const nameRef = useRef(null);
  useEffect(() => {
    // Focus the amount field on edit — the counterparty text stays intact
    // (no highlight/select), because 99% of edits change the amount.
    const el = document.getElementById(`edit-amt-${entry.id}`);
    if (el) {
      el.focus();
      // Select the text so a fresh number replaces the whole value in one
      // keystroke, without wiping the counterparty.
      if (el.select) el.select();
    }
    // Escape-anywhere-in-the-row → cancel. Bound at the row level so it fires
    // even when the counterparty combobox has focus (its internal handler
    // consumes Escape only to close its own popover).
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [entry.id]);

  // Enter from ANY field saves. Explicitly no field-to-field chain here —
  // matches user expectation: "Enter = save, Esc = cancel."
  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    }
  };

  return (
    <div
      className="grid grid-cols-12 gap-2 px-4 py-2 items-center row-state row-state--editing"
      data-testid={`edit-row-${entry.id}`}
    >
      <div className="col-span-2 text-xs text-stone-600">
        {new Date(entry.created_at).toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        })}
      </div>
      <div className="col-span-2">
        <CounterpartyCombobox
          ref={nameRef}
          value={row.counterparty}
          onChange={(v) => setRow({ ...row, counterparty: v })}
          options={options}
          onSelect={(o) => {
            setRow({ ...row, counterparty: o.name });
            // After picking a suggestion, keep focus on amount — that's still
            // what the user is most likely to edit next.
            document.getElementById(`edit-amt-${entry.id}`)?.focus();
          }}
          onSubmit={() => onSave()}
          testId={`edit-counterparty-${entry.id}`}
        />
      </div>
      <div className="col-span-2">
        <input
          id={`edit-note-${entry.id}`}
          type="text"
          placeholder="Note"
          value={row.note}
          onChange={(e) => setRow({ ...row, note: e.target.value })}
          onKeyDown={onKey}
          data-testid={`edit-note-${entry.id}`}
          className="w-full px-2 py-1.5 rounded-lg bg-white border border-stone-200 outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 text-base"
        />
      </div>
      <div className="col-span-3">
        <input
          id={`edit-amt-${entry.id}`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={row.amount}
          onChange={(e) => setRow({ ...row, amount: e.target.value })}
          onBlur={() => {
            const s = String(row.amount || "").trim();
            if (!s.startsWith("=")) return;
            const v = evalFormula(s);
            if (v != null) setRow({ ...row, amount: String(v) });
          }}
          onKeyDown={onKey}
          data-testid={`edit-amount-${entry.id}`}
          title="You can type a formula, e.g. =236*4"
          className="w-full px-2 py-1.5 rounded-lg bg-white border border-stone-200 outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40 font-serif text-lg tabular-nums text-right"
        />
      </div>
      <div className="col-span-2" />
      <div className="col-span-1 flex items-center justify-end gap-1">
        <button
          onClick={onSave}
          className="w-7 h-7 rounded-full bg-[color:var(--gold)] text-white inline-flex items-center justify-center hover:brightness-110"
          data-testid={`edit-save-${entry.id}`}
          title="Save (Enter)"
        >
          <Check size={14} strokeWidth={3} />
        </button>
        <button
          onClick={onCancel}
          className="w-7 h-7 rounded-full bg-rose-500 text-white border border-rose-600 inline-flex items-center justify-center hover:bg-rose-600 transition-colors shadow-sm"
          data-testid={`edit-cancel-${entry.id}`}
          title="Cancel (Esc)"
        >
          <X size={14} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function AuditInfo({ entry }) {
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-1.5">
        <User size={11} className="mt-0.5 shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-500">Posted by</div>
          <div className="font-medium">{entry.created_by_name || "—"}</div>
          <div className="text-stone-500">{fmt(entry.created_at)}</div>
        </div>
      </div>
      {entry.modified && (
        <div className="flex items-start gap-1.5 pt-1 border-t border-stone-200/60">
          <User size={11} className="mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-sky-600">Last edited by</div>
            <div className="font-medium">{entry.updated_by_name || "—"}</div>
            <div className="text-stone-500">{fmt(entry.updated_at)}</div>
          </div>
        </div>
      )}
      {entry.tallied && entry.tallied_by_name && (
        <div className="flex items-start gap-1.5 pt-1 border-t border-stone-200/60">
          <Check size={11} className="mt-0.5 shrink-0 text-emerald-700" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700">Tallied by</div>
            <div className="font-medium">{entry.tallied_by_name}</div>
          </div>
        </div>
      )}
      {entry.status === "finalized" && (
        <div className="flex items-start gap-1.5 pt-1 border-t border-stone-200/60">
          <Lock size={11} className="mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Finalized by</div>
            <div className="font-medium">{entry.finalized_by_name || "—"}</div>
            <div className="text-stone-500">{fmt(entry.finalized_at)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function BalanceTile({ label, value, highlight }) {
  const cls =
    value > 0 ? "text-emerald-700" : value < 0 ? "text-rose-700" : "text-stone-600";
  return (
    <div
      className={`rounded-2xl px-4 py-3 ${highlight ? "glass-gold" : "glass"}`}
      data-testid={`balance-tile-${label.toLowerCase()}`}
    >
      <div className="label-tiny">{label} balance</div>
      <div className={`font-serif text-2xl mt-0.5 ${cls}`}>{num(value)}</div>
    </div>
  );
}

function TransferFullBalanceDialog({ open, onClose, onSaved, account, accounts, selectedId }) {
  const [counterId, setCounterId] = useState("");
  const [note, setNote] = useState(`Transfer full balance to zero out ${account.name}`);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const options = useMemo(
    () => accounts.filter((a) => a.id !== selectedId && a.active !== false),
    [accounts, selectedId]
  );

  const submit = async () => {
    setErr("");
    if (!counterId) {
      setErr("Choose a counterparty");
      return;
    }
    setBusy(true);
    try {
      // Post entry: from = selected, to = counterparty, amount = closing_balance
      // This makes selected's balance become 0 and counterparty's balance changes by closing_balance.
      await api.post("/entries", {
        from_account_id: selectedId,
        to_account_id: counterId,
        amount: account.closing_balance,
        note,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const counter = options.find((a) => a.id === counterId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-strong rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Zero out balance</DialogTitle>
          <DialogDescription>
            Transfer the full closing balance of{" "}
            <b>{account.name}</b> to another account. This posts a single entry that makes{" "}
            <b>{account.name}</b>&apos;s balance zero.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="glass rounded-2xl p-4 text-center">
            <div className="label-tiny">Amount to transfer</div>
            <div
              className={`font-serif text-4xl mt-1 ${
                account.closing_balance > 0
                  ? "text-emerald-700"
                  : account.closing_balance < 0
                    ? "text-rose-700"
                    : "text-stone-600"
              }`}
              data-testid="transfer-amount-preview"
            >
              {num(account.closing_balance)}
            </div>
          </div>
          <div>
            <label className="label-tiny">To account</label>
            <Select value={counterId} onValueChange={setCounterId}>
              <SelectTrigger className="input-glass mt-1" data-testid="transfer-counter-select">
                <SelectValue placeholder="Choose counterparty…" />
              </SelectTrigger>
              <SelectContent className="glass-strong rounded-xl max-h-72">
                {options.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}{" "}
                    <span className="text-xs text-stone-500 ml-2">({num(a.balance)})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {counter && (
            <div className="glass rounded-2xl p-3 text-xs text-stone-600">
              <div className="flex items-center justify-between">
                <span>
                  {account.name} <b className="font-serif">{num(account.closing_balance)}</b>
                </span>
                <span className="text-stone-400">→</span>
                <span>
                  {counter.name} <b className="font-serif">{num(counter.balance)}</b>
                </span>
              </div>
              <div className="flex items-center justify-between mt-1 pt-1 border-t border-stone-200/60">
                <span>
                  Becomes <b className="font-serif text-stone-500">0.00</b>
                </span>
                <span className="text-stone-400">→</span>
                <span>
                  Becomes{" "}
                  <b className="font-serif">
                    {num(counter.balance + account.closing_balance)}
                  </b>
                </span>
              </div>
            </div>
          )}
          <div>
            <label className="label-tiny">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input-glass mt-1"
              data-testid="transfer-note-input"
            />
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50/70 border border-red-100 rounded-xl px-3 py-2">
              {err}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !counterId}
              className="btn-gold flex-1"
              data-testid="transfer-confirm-btn"
            >
              {busy ? "Posting…" : "Transfer & zero out"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
